/**
 * Mapbox GL map instance singleton.
 * Initialised by features/map/MapCanvas; layers attached via lib/map/layers/*.
 */
import mapboxgl, { Map } from 'mapbox-gl';
import { config } from '@/lib/config';
import { getStartCenter } from '@/lib/map/userLocation';
import type { LngLat } from '@/lib/oi/searchArea';

mapboxgl.accessToken = config.map.mapboxToken;

let mapInstance: Map | null = null;
let resizeObserver: ResizeObserver | null = null;

/**
 * True while the map is mid-animation (`movestart` → `moveend`). Marker
 * subscribers read this and skip `setLngLat` so live MQTT motion doesn't
 * fight the zoom/pan animation — the user perceives drift otherwise.
 */
let _animating = false;
const animListeners = new Set<(v: boolean) => void>();

export const mapAnimation = {
  get value() { return _animating; },
  subscribe(fn: (v: boolean) => void) {
    animListeners.add(fn);
    return () => animListeners.delete(fn);
  },
  set(v: boolean) {
    if (_animating === v) return;
    _animating = v;
    for (const l of animListeners) l(v);
  },
};

export function createMap(container: HTMLElement, opts: { style?: string; center?: [number, number]; zoom?: number } = {}): Map {
  if (mapInstance) return mapInstance;
  // Last known user location, else the tenant default — ENG-277.
  const [lat, lng] = getStartCenter();
  mapInstance = new mapboxgl.Map({
    container,
    style: opts.style ?? config.map.defaultStyle,
    center: [opts.center?.[1] ?? lng, opts.center?.[0] ?? lat],
    zoom: opts.zoom ?? config.map.defaultZoom,
    attributionControl: false,
  });
  // Mapbox measures at construction time; if container had no layout yet, the canvas
  // ends up 0×0. Observe and resize.
  const m = mapInstance;
  resizeObserver = new ResizeObserver(() => m.resize());
  resizeObserver.observe(container);
  // Force a resize next frame to catch the initial layout pass
  requestAnimationFrame(() => m.resize());

  // Track animation state so markers can pause setLngLat during zoom/pan.
  m.on('movestart', () => mapAnimation.set(true));
  m.on('moveend', () => mapAnimation.set(false));
  m.on('zoomstart', () => mapAnimation.set(true));
  m.on('zoomend', () => mapAnimation.set(false));

  if (import.meta.env.DEV) {
    (window as unknown as { __flysafeMap?: Map }).__flysafeMap = m;
  }

  return mapInstance;
}

export function getMap(): Map | null {
  return mapInstance;
}

/** Current map centre as [lng, lat]; the start centre (see userLocation.ts)
 *  before the map exists. `getStartCenter()` is [lat, lng]. */
export function getMapCenter(): LngLat {
  const c = mapInstance?.getCenter();
  if (c) return [c.lng, c.lat];
  const [lat, lng] = getStartCenter();
  return [lng, lat];
}

export function disposeMap() {
  if (resizeObserver) {
    resizeObserver.disconnect();
    resizeObserver = null;
  }
  if (mapInstance) {
    mapInstance.remove();
    mapInstance = null;
  }
}
